import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Alert from '@mui/lab/Alert';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Container from '@mui/material/Container';
import newsletterSubscriptionMutation from './newsletterSubscription.graphql';
import hubspotFormBlockShape from 'shapes/components/hubspotFormBlockShape';
import RichTextField from 'components/RichTextField';
import Button from 'components/Button';
import Input, { FormControl } from 'components/Input';
import Checkbox from 'components/Checkbox';
import { dataLayerPush } from 'utils/dataLayer';

const FormHeader = styled.h2`
  text-align: left;
  font-size: 2rem;
  font-weight: 700;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
`;

const OuterContainer = styled(Container)`
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  ${props => props.theme.breakpoints.up('lg')} {
    max-width: 1380px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const FieldWrapper = styled.div`${({ theme }) => css`
  margin-bottom: 1.125rem;

  ${props => props.$isCheckbox && `
    grid-column: 1 / -1;
    padding-bottom: 0.5rem;
  `}

  ${FormControl} {
    @media (max-width: ${theme.breakpoints.custom.mobileLg}) {
      width: 100%;
    }
  }
`}`;

const InputFieldsGrid = styled.div`
  display: grid;

  @media (max-width: 959px) {
    grid-template-columns: 1fr;
  }
`;

const RichText = styled(RichTextField)`
  margin: 0;

  p {
    margin-bottom: 0;
    margin-top: 0;
  }
`;

const AlertBanner = styled(Alert)`
  margin-bottom: 0.5rem;

  ${RichText} {
    p {
      font-size: 0.875rem;
    }
  }
`

HubspotFormBlock.propTypes = {
  className: PropTypes.string,
  data: hubspotFormBlockShape
};

function HubspotFormBlock({ data, className, ...props }) {
  const { header, formFields, subscribeButton, termsAndServices, hubspotAlertMessage } = data;
  const [formData, setFormData] = useState({});
  const [honeypot, setHoneypot] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessages, setAlertMessages] = useState([]);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [formKey, setFormKey] = useState(0);
  const [newsletterSubscription] = useMutation(newsletterSubscriptionMutation);
  const emailRegex = /^[^\s@]+@[^\s@\.]+\.[^\s@\.]{2,}$/;

  const buildMutationInput = () => {
    const email = formData.email;
    const firstName = formData.firstname;
    const lastName = formData.lastname;
    const zipCode = formData.zip_code;
    const emailSubscriptions = formFields
      .filter(field => field.formType === 'checkbox' && !!formData[field.hubspotPropertyName])
      .map(field => {
        return {
          id: field.hubspotPropertyName,
          status: 'OPTED_IN'
        };
      });

    return { email, emailSubscriptions, firstName, lastName, zipCode };
  };

  const handleInputChange = (e, propertyName = null) => {
    const { name, type, checked, value } = e.target;
    const key = propertyName || name;

    setFormData(prev => {
      if (type === 'checkbox') {
        if (checked) {
          return { ...prev, [key]: true };
        } else {
          const newData = { ...prev };
          delete newData[key];
          return newData;
        }
      } else {
        return { ...prev, [key]: value };
      }
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlertMessages([]);
    setAlertSeverity('error');
    dataLayerPush('Submitted form to subscribe to newsletter', 'event');

    const GENERIC_ERROR_MSG = 'Uh oh, something went wrong with submitting your sign up request. Please try again.';
    const SUCCESS_MSG = 'Success! Thanks for subscribing!';

    if (honeypot) {
      setIsSubmitting(false);
      return;
    }

    const input = buildMutationInput();
    const validationErrors = [];

    // Validate email
    if (!input.email || !emailRegex.test(input.email)) {
      validationErrors.push('Please enter a valid email address.');
    }

    // Validate at least one checkbox selected
    const hasAtLeastOneSubscribed = input.emailSubscriptions.some(sub => sub.status === 'OPTED_IN');
    if (!hasAtLeastOneSubscribed) {
      const checkboxValidationMessage = hubspotAlertMessage?.[0]?.checkboxValidationError || 'You must select at least one subscription.';
      validationErrors.push(checkboxValidationMessage);
    }

    if (validationErrors.length > 0) {
      setAlertMessages(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      await newsletterSubscription({ variables: { input } });
      dataLayerPush('Newsletter subscription completed successfully', 'event');
      setAlertMessages([SUCCESS_MSG]);
      setAlertSeverity('success');
      setFormData({});
      setFormKey(prev => prev + 1);
    } catch (err) {
      setAlertMessages([GENERIC_ERROR_MSG]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OuterContainer className={className} maxWidth='xl'>
      {header && <FormHeader>{header}</FormHeader>}

      <form onSubmit={handleSubmit} key={formKey}>
        <InputFieldsGrid>
          {formFields.map((field, index) => {
            const {
              formType,
              hubspotPropertyName,
              required,
              fieldLabel
            } = field;

            const isCheckbox = formType === 'checkbox';

            return (
              <FieldWrapper key={index} $isCheckbox={isCheckbox}>
                {isCheckbox ? (
                  <Checkbox
                    id={hubspotPropertyName}
                    name={hubspotPropertyName}
                    label={<RichText richText={fieldLabel} $disableMargin />}
                    checked={!!formData[hubspotPropertyName]}
                    onChange={e => handleInputChange(e, hubspotPropertyName)}
                    type='checkbox'
                    verticalAlign='top'
                  />
                ) : (
                  <Input
                    id={hubspotPropertyName}
                    name={hubspotPropertyName}
                    label={<RichText richText={fieldLabel} $disableMargin />}
                    type={formType || 'text'}
                    required={required}
                    showAsterisk={required}
                    onChange={handleInputChange}
                    value={formData[hubspotPropertyName] || ''}
                    width='half'
                    labelFlex
                    labelMarginBottom="0"
                  />
                )}
              </FieldWrapper>
            );
          })}
        </InputFieldsGrid>

          {/* Honeypot */}
          <input
            type='checkbox'
            id='contact_me'
            name='contact_me'
            onChange={(e) => setHoneypot(e.target.checked)}
            style={{ display: 'none' }}
            tabIndex='-1'
            autoComplete='off'
          />

          {termsAndServices && (
            <RichText richText={termsAndServices} $disableMargin />
          )}

          <Button type='submit' variant='contained' color='primary' disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : subscribeButton || 'Subscribe'}
          </Button>

        {alertMessages.length > 0 && (
          <Box mt={2}>
            {alertMessages.map((msg, i) => (
              <AlertBanner key={i} severity={alertSeverity}>
                <RichText richText={msg} $disableMargin />
              </AlertBanner>
            ))}
          </Box>
        )}
      </form>
    </OuterContainer>
  );
}

export default HubspotFormBlock;
